"use client";

import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import { transactionQueryKeys, getTransactions } from "./transaction-reads";
import type { GetTransactionsRequestParameter, GetTransactionsResponse, TransactionDetailPublicDto } from "./transaction-reads";
import { TRANSACTION_LIST_STALE_TIME, TRANSACTION_DETAIL_STALE_TIME } from "@donista/shared/constants/transactions";
import { apiAuth } from "@/openapi/client-auth";

export function useTransactions(
    params?: GetTransactionsRequestParameter,
    options?: Omit<UseQueryOptions<GetTransactionsResponse>, "queryKey" | "queryFn">
) {
    return useQuery({
        queryKey: transactionQueryKeys.list(params),
        queryFn: () => getTransactions(params),
        staleTime: TRANSACTION_LIST_STALE_TIME,
        ...options,
    });
}

export function useUserTransactions(
    options?: Omit<UseQueryOptions<GetTransactionsResponse>, "queryKey" | "queryFn">
) {
    return useQuery({
        queryKey: transactionQueryKeys.userList(),
        queryFn: async () => {
            const { data } = await apiAuth.GET("/transactions", {
                params: { query: { size: 20 } },
            });
            return data as GetTransactionsResponse;
        },
        staleTime: TRANSACTION_LIST_STALE_TIME,
        ...options,
    });
}

/**
 * Whether the signed-in user has any unattributed user-facing transaction.
 * Gates the one-time "first project" backfill warning on the project-selection surfaces.
 * Pass `enabled: !!user` from the caller so it only runs when signed in.
 */
export function useHasUnattributedTransactions(
    options?: Omit<UseQueryOptions<boolean>, "queryKey" | "queryFn">
) {
    return useQuery({
        queryKey: transactionQueryKeys.hasUnattributed(),
        queryFn: async () => {
            const { data } = await apiAuth.GET("/transactions/has-unattributed");
            return data?.hasUnattributed ?? false;
        },
        staleTime: TRANSACTION_LIST_STALE_TIME,
        ...options,
    });
}

export function useTransactionDetail(
    id: string,
    options?: Omit<UseQueryOptions<TransactionDetailPublicDto>, "queryKey" | "queryFn">
) {
    return useQuery({
        queryKey: transactionQueryKeys.detail(id),
        queryFn: async () => {
            const { data } = await apiAuth.GET("/transactions/{id}", {
                params: { path: { id } },
            });
            return data as TransactionDetailPublicDto;
        },
        staleTime: TRANSACTION_DETAIL_STALE_TIME,
        ...options,
    });
}
